"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestACustomQuote() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return;
}
function QuoteFormContent({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8 space-y-4">
        <div className="w-16 h-16 mx-auto bg-accent/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-foreground-dark text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 btn-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must be less than 50 characters"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "0ms"
      }}>
              <FormLabel className="text-foreground-dark">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jordan Smith" {...field} className="bg-dark-background/50 border-border text-foreground-dark focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "80ms"
      }}>
              <FormLabel className="text-foreground-dark">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jordan@example.com" {...field} className="bg-dark-background/50 border-border text-foreground-dark focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "160ms"
      }}>
              <FormLabel className="text-foreground-dark">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(303) 555-1234" {...field} className="bg-dark-background/50 border-border text-foreground-dark focus-visible:border-primary" />
              </FormControl>
              <FormDescription className="text-muted-dark">
                We'll call you to discuss details
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_date" rules={{
        required: "Event date is required"
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "240ms"
      }}>
              <FormLabel className="text-foreground-dark">Event Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="bg-dark-background/50 border-border text-foreground-dark focus-visible:border-primary" />
              </FormControl>
              <FormDescription className="text-muted-dark">
                Select the date of your wedding or event
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_type" rules={{
        required: "Please select an event type"
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "320ms"
      }}>
              <FormLabel className="text-foreground-dark">Event Type</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-border bg-dark-background/50 px-3 py-1 text-base text-foreground-dark shadow-xs transition-all outline-none focus-visible:border-primary focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                  <option value="">Select event type</option>
                  <option value="Wedding">Wedding</option>
                  <option value="Engagement Party">Engagement Party</option>
                  <option value="Reception">Reception</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="Other">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "400ms"
      }}>
              <FormLabel className="text-foreground-dark">Estimated Budget</FormLabel>
              <FormControl>
                <Input type="number" placeholder="5000" {...field} className="bg-dark-background/50 border-border text-foreground-dark focus-visible:border-primary" />
              </FormControl>
              <FormDescription className="text-muted-dark">
                Optional – helps us tailor the proposal
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="details" render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "480ms"
      }}>
              <FormLabel className="text-foreground-dark">Additional Details</FormLabel>
              <FormControl>
                <textarea placeholder="Tell us about your music preferences, lighting needs, etc." {...field} rows={4} className="flex min-h-[80px] w-full rounded-md border border-border bg-dark-background/50 px-3 py-2 text-base text-foreground-dark shadow-xs transition-all outline-none focus-visible:border-primary focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="w-full btn-primary group relative overflow-hidden">
            <span className="relative z-10 flex items-center justify-center gap-2">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-4" />
                  Sending Your Request...
                </> : "Request Your Custom Quote"}
            </span>
            <span className="absolute inset-0 bg-accent-hover opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)]"></span>
          </Button>
        </div>
      </div>
    </Form>;
}